import { RequestError } from '@/renderer/utils/errors';

/**
 * Turns raw server validation text into member-facing copy (#2142 O2).
 *
 * The API validates with Joi, whose messages quote the field key
 * (`"email" must be a valid email`), and the request wrapper surfaces the
 * generic `Validation Failed` / unique-violation errors verbatim. Forms run
 * field and form-level messages through here so members never see quoted keys
 * or developer wording. Unknown messages pass through with the quoted key
 * replaced by a readable label.
 */

const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  firstName: 'First name',
  lastName: 'Last name',
  password: 'Password',
  username: 'Username',
  contactSecondary: 'Email',
  contactTertiary: 'Email',
  code: 'Code',
  emailCode: 'Code',
  contactSecondaryCode: 'Code',
  contactTertiaryCode: 'Code',
};

const humanize = (key: string) =>
  FIELD_LABELS[key] ||
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

export const VALID_EMAIL_MESSAGE = 'Enter a valid email address, like name@example.com.';
export const EMAIL_TAKEN_MESSAGE =
  'An account with this email already exists. Sign in or reset your password instead.';
// Also what a repeat sign-up with the SAME email hits first (same derived
// username), so it has to fit both cases without claiming either.
export const USERNAME_TAKEN_MESSAGE =
  "We couldn't create an account with this email. If you've signed up before, sign in or reset your password. Otherwise, try a different email or contact support.";

const RAW_ERROR_NAMES = ['DBError', 'DataError', 'UniqueViolationError'];

/** A server-sent message that reads as copy rather than an internal token or DB text. */
const usableServerMessage = (data: { name?: string; message?: unknown }) =>
  typeof data?.message === 'string' &&
  !!data.message.trim() &&
  !/^API\./.test(data.message) &&
  !RAW_ERROR_NAMES.includes(`${data.name}`);

/** Friendly copy for one field-level validation message. */
export function friendlyFieldMessage(message: string | undefined, field?: string): string {
  const raw = `${message || ''}`.trim();

  if (!raw) {
    return field ? `${humanize(field)} is required.` : 'Please check this field.';
  }

  // Joi messages start with the quoted key: "email" must be a valid email
  const quoted = raw.match(/^"([^"]+)"\s+(.*)$/);
  const key = quoted?.[1] || field || '';
  const rest = quoted ? quoted[2] : raw;
  const label = key ? humanize(key) : 'This field';

  if (/must be a valid email/i.test(rest) || /not a valid email/i.test(rest)) {
    return VALID_EMAIL_MESSAGE;
  }
  if (/is not allowed to be empty|is required/i.test(rest)) {
    return `${label} is required.`;
  }
  const min = rest.match(/length must be at least (\d+)/i);
  if (min) {
    return `${label} must be at least ${min[1]} characters.`;
  }
  const max = rest.match(/length must be less than or equal to (\d+)/i);
  if (max) {
    return `${label} must be ${max[1]} characters or fewer.`;
  }
  if (/must only contain alpha-numeric/i.test(rest)) {
    return `${label} can only use letters and numbers.`;
  }

  return quoted ? `${label} ${rest}`.replace(/\.?$/, '.') : raw;
}

/**
 * Friendly copy for a whole request error, or null when the error is fully
 * explained by field-level messages already shown next to the inputs.
 */
export function friendlyRequestError(error: unknown, visibleFields: string[] = []): string | null {
  if (!error) {
    return null;
  }

  if (error instanceof RequestError) {
    const data = error.data || {};
    const details: { path?: (string | number)[]; message?: string }[] | undefined =
      data.details?.body || data.details?.query;

    if (Array.isArray(details) && details.length) {
      const unshown = details.filter((d) => !visibleFields.includes(`${d.path?.join('.')}`));
      if (!unshown.length) {
        // Every problem is already shown on its field.
        return null;
      }
      return unshown
        .map((d) => friendlyFieldMessage(d.message, `${d.path?.join('.') || ''}`))
        .join(' ');
    }

    if (data.name === 'UniqueViolationError') {
      // useFormSubmit already shows this on the field when it is on the form.
      if (data.columns?.some((column: string) => visibleFields.includes(column))) {
        return null;
      }
      if (data.columns?.includes('email')) {
        return EMAIL_TAKEN_MESSAGE;
      }
      // Sign-up derives the username from the email with punctuation stripped,
      // so a.b@x.com and ab@x.com collide. That is not "your email exists".
      if (data.columns?.includes('username')) {
        return USERNAME_TAKEN_MESSAGE;
      }
    }

    if (error.code === 429) {
      return 'Too many attempts. Please wait a minute and try again.';
    }

    // Keep a meaningful server message; only replace missing, token-style
    // (API.X.Y) or raw database text with the generic retry copy.
    if (error.code >= 500 && !usableServerMessage(data)) {
      return 'Something went wrong on our side. Please try again in a moment.';
    }
  }

  const message = error instanceof Error ? error.message : String(error);

  if (/^validation failed$/i.test(message)) {
    return 'Please check the highlighted fields.';
  }

  return friendlyFieldMessage(message);
}
